<template>
  <div class="guest-home">
    <AppHeader active="overview" @locked="showLocked" />

    <main class="up-container up-page guest-home__content">
      <section class="guest-hero">
        <div class="guest-hero__copy">
          <div class="guest-hero__status">
            <i />
            {{ publicStatus }}
          </div>
          <h1>让专业策略，替你交易</h1>
          <p>AI 多策略自动交易，一个账户管理全部交易所、机器人与资金流向，收益与费用透明可查。</p>
          <div class="guest-hero__actions">
            <button class="guest-hero__primary" type="button" @click="$router.push('/register')">
              立即免费注册
            </button>
            <button class="guest-hero__secondary" type="button" @click="$router.push('/login')">
              登录查看我的资产
            </button>
          </div>
          <div class="guest-hero__trust">
            <span>银行级加密存储</span>
            <span>资金不托管</span>
            <span>API 交易授权</span>
            <span>公开数据实时同步</span>
          </div>
        </div>

        <div class="guest-hero__metrics">
          <LiveMetricCard
            label="平台累计结算收益 (USDT)"
            :value="formattedRevenue"
            :caption="publicError ? '公开数据暂时不可用' : '实时更新'"
            live
          />
          <LiveMetricCard label="平台累计收益率" :value="formattedYield" unit="%" live />
          <LiveMetricCard label="运行中机器人" :value="formattedRunningRobotCount" unit="台" />
          <LiveMetricCard label="稳定运行天数" :value="formattedRunDays" unit="天" />
        </div>
      </section>

      <section class="trade-ticker" aria-label="实时成交">
        <span class="trade-ticker__label"><i />实时成交</span>
        <div class="trade-ticker__viewport">
          <div class="trade-ticker__track">
            <span>{{ tickerText }}</span>
            <span>{{ tickerText }}</span>
          </div>
        </div>
      </section>

      <section id="strategies" class="guest-section">
        <div class="guest-section__heading">
          <div>
            <h2>策略产品收益榜</h2>
            <p>登录后查看你的策略收益</p>
          </div>
          <button type="button" @click="$router.push('/register')">注册后创建策略 →</button>
        </div>
        <div class="strategy-grid">
          <StrategyCard
            v-for="strategy in strategies"
            :key="strategy.name"
            :strategy="strategy"
            @action="$router.push('/register')"
          />
        </div>
      </section>

      <section class="guest-insight-grid">
        <article class="asset-preview up-card">
          <h2>我的资产总览</h2>
          <div class="asset-preview__blur">
            <div class="asset-preview__numbers">
              <span><small>总资产 (USDT)</small><strong>--</strong></span>
              <span><small>今日收益 (USDT)</small><strong>--</strong></span>
              <span><small>累计收益 (USDT)</small><strong>--</strong></span>
            </div>
            <div class="asset-preview__empty">登录后查看收益曲线</div>
          </div>
          <div class="asset-preview__gate">
            <span class="asset-preview__lock"><i class="el-icon-lock" /></span>
            <strong>登录后查看你的专属收益曲线</strong>
            <button class="up-primary-button" type="button" @click="$router.push('/login')">
              登录查看
            </button>
          </div>
        </article>

        <article class="leaderboard up-card">
          <div class="leaderboard__head">
            <h2>本周收益榜</h2>
            <span>实时榜单</span>
          </div>
          <div v-for="leader in leaders" :key="`${leader.rank}-${leader.name}`" class="leaderboard__row">
            <span class="leaderboard__rank" :class="`rank-${leader.rank}`">{{ leader.rank }}</span>
            <span class="leaderboard__name">{{ leader.name }}</span>
            <span class="leaderboard__strategy">{{ leader.strategy }}</span>
            <strong>{{ leader.amount }}</strong>
          </div>
          <div v-if="!leaders.length" class="leaderboard__empty">
            {{ loading ? '正在加载公开榜单…' : '暂无公开榜单数据' }}
          </div>
          <button type="button" @click="$router.push('/register')">注册后上榜 →</button>
        </article>
      </section>

      <section class="trust-grid">
        <article v-for="item in trustItems" :key="item.title" class="up-card trust-card">
          <span class="trust-card__icon"><i :class="item.icon" /></span>
          <div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </div>
        </article>
      </section>
    </main>

    <AppFooter />

    <ToastMessage :visible="toast.visible" :message="toast.message" :type="toast.type" />
  </div>
</template>

<script>
import AppFooter from '@/components/rebuild/AppFooter.vue'
import AppHeader from '@/components/rebuild/AppHeader.vue'
import LiveMetricCard from '@/components/rebuild/LiveMetricCard.vue'
import StrategyCard from '@/components/rebuild/StrategyCard.vue'
import ToastMessage from '@/components/rebuild/ToastMessage.vue'
import { loadPublicSnapshot } from '@/api/portal'
import {
  firstNumber,
  formatNumber,
  formatRate,
  mapLeader,
  maskAccount
} from '@/utils/portal-data'

export default {
  name: 'GuestHome',
  components: {
    AppFooter,
    AppHeader,
    LiveMetricCard,
    StrategyCard,
    ToastMessage
  },
  data() {
    return {
      platform: null,
      publicLeaders: [],
      publicTrades: [],
      publicError: '',
      loading: true,
      toastTimer: null,
      toast: { visible: false, message: '', type: 'info' },
      strategies: [
        {
          name: 'UPlus-Astra AI',
          tag: 'PRIME · 旗舰',
          tagColor: '#b7791f',
          description: 'AI 智能选择指标并自动运行。',
          risk: '中高',
          riskClass: '',
          return30: '--',
          drawdown: '--',
          bots: '--',
          aum: '--',
          points: []
        },
        {
          name: 'UPlus-X pro',
          tag: 'AI 合约策略',
          tagColor: '#ff7a1a',
          description: '适合主流币种与趋势行情的 AI 合约交易。',
          risk: '中',
          riskClass: '',
          return30: '--',
          drawdown: '--',
          bots: '--',
          aum: '--',
          points: []
        },
        {
          name: 'UPlus-X',
          tag: '风险控制',
          tagColor: '#0b7a47',
          description: '强调风险控制的合约策略。',
          risk: '低',
          riskClass: 'low',
          return30: '--',
          drawdown: '--',
          bots: '--',
          aum: '--',
          points: []
        },
        {
          name: '合约 AI 交易',
          tag: '多空对冲',
          tagColor: '#1f6bff',
          description: '以多空对冲方式执行合约交易。',
          risk: '中高',
          riskClass: '',
          return30: '--',
          drawdown: '--',
          bots: '--',
          aum: '--',
          points: []
        }
      ],
      trustItems: [
        {
          title: '资金不托管',
          icon: 'el-icon-lock',
          description: '资产保留在用户自己的交易所账户，通过授权执行策略。'
        },
        {
          title: '数据实时更新',
          icon: 'el-icon-time',
          description: '账户、策略和收益数据实时更新，状态清晰可见。'
        },
        {
          title: '费用记录可查',
          icon: 'el-icon-data-analysis',
          description: '登录后可查询资产与点卡的实际流水。'
        }
      ]
    }
  },
  computed: {
    formattedRevenue() {
      return formatNumber(firstNumber(this.platform, ['profit']))
    },
    formattedYield() {
      const value = firstNumber(this.platform, ['profit_yield'])
      return value === null ? '--' : formatRate(value).replace('%', '')
    },
    formattedRunningRobotCount() {
      const value = firstNumber(this.platform, ['running_robot_count'])
      return value === null ? '--' : formatNumber(value, 0)
    },
    formattedRunDays() {
      const value = firstNumber(this.platform, ['run_days'])
      return value === null ? '--' : formatNumber(value, 0)
    },
    publicStatus() {
      if (this.loading) return this.$t('正在同步公开数据')
      if (this.publicError) return this.$t('公开数据暂时不可用')
      return this.$t('公开数据已同步')
    },
    tickerText() {
      const source = this.publicTrades
      if (!source.length) return this.loading ? this.$t('正在加载公开成交数据…') : this.$t('暂无公开成交数据')
      return source.slice(0, 12).map(item => {
        const account = maskAccount(item.profit_user || item.username || item.name || item.mobile || item.address)
        const tradeCount = firstNumber(item, ['trade_count', 'count', 'profit_times', 'order_count', 'deal_count'])
        const profit = firstNumber(item, ['profit_withdraw', 'trade_profit', 'profit', 'profit_total', 'income'])
        const metric = tradeCount !== null
          ? `${formatNumber(tradeCount, 0)} 单`
          : profit !== null ? `${profit > 0 ? '+' : ''}${formatNumber(profit)} USDT` : '--'
        return `${account} · ${metric}`
      }).join('　　')
    },
    leaders() {
      return this.publicLeaders.slice(0, 5).map((item, index) => mapLeader(item, index))
    }
  },
  mounted() {
    this.fetchPublicData()
  },
  beforeDestroy() {
    window.clearTimeout(this.toastTimer)
  },
  methods: {
    async fetchPublicData() {
      this.loading = true
      const snapshot = await loadPublicSnapshot()
      this.platform = snapshot.platform
      this.publicLeaders = snapshot.leaders
      this.publicTrades = snapshot.trades
      this.publicError = snapshot.errors.join('；')
      this.loading = false
    },
    scrollToStrategies() {
      document.getElementById('strategies').scrollIntoView({ behavior: 'smooth' })
    },
    showLocked() {
      window.clearTimeout(this.toastTimer)
      this.toast = {
        visible: true,
        message: this.$t('登录后可查看，请先登录或注册'),
        type: 'info'
      }
      this.toastTimer = window.setTimeout(() => {
        this.toast.visible = false
      }, 2600)
    }
  }
}
</script>

<style scoped>
.guest-home {
  display: flex;
  min-height: 100vh;
  flex-direction: column;
}

.guest-home__content {
  flex: 1 0 auto;
  padding-top: 28px;
  padding-bottom: 80px;
}

.guest-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  margin-bottom: 20px;
  padding: 40px 44px;
  color: #fff;
  border-radius: 16px;
  background: radial-gradient(900px 420px at 88% -30%, rgba(74, 140, 255, 0.4), transparent 62%),
    radial-gradient(700px 340px at -8% 130%, rgba(31, 107, 255, 0.35), transparent 60%),
    linear-gradient(130deg, #081737, #123289 58%, #1d5bd8);
  box-shadow: 0 20px 48px rgba(8, 23, 55, 0.22);
}

.guest-hero__copy {
  max-width: 590px;
}

.guest-hero__status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 12.5px;
  font-weight: 700;
}

.guest-hero__status i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #9fe8c3;
  animation: up-pulse 2s ease infinite;
}

.guest-hero h1 {
  margin: 18px 0 0;
  font-size: clamp(34px, 2.7vw, 44px);
  line-height: 1.4;
  letter-spacing: 0.5px;
}

.guest-hero__copy > p {
  max-width: 560px;
  margin: 14px 0 0;
  color: rgba(255, 255, 255, 0.74);
  font-size: 15px;
  line-height: 1.8;
}

.guest-hero__actions {
  display: flex;
  gap: 12px;
  margin-top: 26px;
}

.guest-hero__actions button {
  padding: 12px 24px;
  color: #fff;
  border: 1px solid transparent;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 800;
}

.guest-hero__primary {
  color: #4a3403 !important;
  background: linear-gradient(135deg, #f9e3a0, #edbe4b);
  box-shadow: 0 8px 22px rgba(237, 190, 75, 0.35);
}

.guest-hero__secondary {
  border-color: rgba(255, 255, 255, 0.4) !important;
  background: rgba(255, 255, 255, 0.09);
}

.guest-hero__trust {
  display: flex;
  gap: 10px;
  margin-top: 24px;
  color: rgba(255, 255, 255, 0.66);
  font-size: 12px;
}

.guest-hero__trust span:not(:last-child)::after {
  margin-left: 10px;
  color: rgba(255, 255, 255, 0.4);
  content: "·";
}

.guest-hero__metrics {
  display: grid;
  width: 520px;
  grid-template-columns: repeat(2, minmax(200px, 1fr));
  gap: 14px;
  flex: 0 0 auto;
}

.trade-ticker {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 26px;
  padding: 11px 20px;
  overflow: hidden;
  border: 1px solid var(--up-line);
  border-radius: 12px;
  background: #fff;
}

.trade-ticker__label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 12px;
  color: #0b7a47;
  border-radius: 999px;
  background: #e8f6ee;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.trade-ticker__label i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #12b76a;
  animation: up-pulse 2s ease infinite;
}

.trade-ticker__viewport {
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.trade-ticker__track {
  display: inline-flex;
  color: #5a6072;
  animation: up-ticker 24s linear infinite;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.trade-ticker__track span {
  padding-right: 60px;
}

.guest-section__heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 14px;
}

.guest-section__heading h2,
.asset-preview h2,
.leaderboard h2 {
  margin: 0;
  font-size: 18px;
}

.guest-section__heading p {
  margin: 4px 0 0;
  color: #8a90a0;
  font-size: 12.5px;
}

.guest-section__heading button,
.leaderboard > button {
  padding: 0;
  color: var(--up-blue);
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
}

.strategy-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
}

.guest-insight-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 400px;
  gap: 20px;
  margin-top: 26px;
}

.asset-preview {
  position: relative;
  min-height: 342px;
  overflow: hidden;
  padding: 22px;
}

.asset-preview__blur {
  margin-top: 16px;
  filter: blur(7px);
  pointer-events: none;
  user-select: none;
}

.asset-preview__empty,
.leaderboard__empty {
  display: grid;
  min-height: 132px;
  place-items: center;
  color: #98a0af;
  font-size: 12px;
}

.leaderboard__empty {
  min-height: 180px;
}

.asset-preview__numbers {
  display: flex;
  gap: 36px;
  margin-bottom: 10px;
}

.asset-preview__numbers small,
.asset-preview__numbers strong {
  display: block;
}

.asset-preview__numbers small {
  color: #8a90a0;
  font-size: 12.5px;
}

.asset-preview__numbers strong {
  margin-top: 2px;
  color: var(--up-success);
  font-size: 26px;
}

.asset-preview__gate {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: rgba(255, 255, 255, 0.35);
}

.asset-preview__lock {
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  color: var(--up-blue);
  border-radius: 12px;
  background: var(--up-blue-soft);
  font-size: 20px;
}

.leaderboard {
  display: flex;
  flex-direction: column;
  padding: 22px;
}

.leaderboard__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.leaderboard__head span {
  color: #98a0af;
  font-size: 12px;
}

.leaderboard__row {
  display: grid;
  grid-template-columns: 28px 72px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 11px 10px;
  border-radius: 10px;
}

.leaderboard__row:hover {
  background: #f0f5fe;
}

.leaderboard__rank {
  display: grid;
  width: 28px;
  height: 28px;
  place-items: center;
  color: #5a6072;
  border-radius: 50%;
  background: #f4f5f9;
  font-size: 13px;
  font-weight: 800;
}

.leaderboard__rank.rank-1 {
  color: #b7791f;
  background: #fdf0d0;
}

.leaderboard__rank.rank-2 {
  color: #5a6478;
  background: #edf0f6;
}

.leaderboard__rank.rank-3 {
  color: #b4560b;
  background: #fbe8da;
}

.leaderboard__name {
  font-size: 14px;
  font-weight: 700;
}

.leaderboard__strategy {
  overflow: hidden;
  color: #98a0af;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.leaderboard__row strong {
  color: var(--up-success);
  font-size: 14px;
}

.leaderboard > button {
  margin-top: auto;
  padding-top: 14px;
  border-top: 1px solid #f3f4f8;
}

.trust-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 26px;
}

.trust-card {
  display: flex;
  gap: 14px;
  padding: 22px;
}

.trust-card__icon {
  display: grid;
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  place-items: center;
  color: var(--up-blue);
  border-radius: 11px;
  background: var(--up-blue-soft);
  font-size: 18px;
}

.trust-card h3 {
  margin: 0;
  font-size: 14.5px;
}

.trust-card p {
  margin: 4px 0 0;
  color: var(--up-muted);
  font-size: 12.5px;
  line-height: 1.7;
}

@media (max-width: 1260px) {
  .guest-hero {
    align-items: stretch;
    flex-direction: column;
  }

  .guest-hero__metrics {
    width: 100%;
  }

  .strategy-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 900px) {
  .guest-home__content {
    padding-top: 16px;
  }

  .guest-hero {
    padding: 28px 22px;
  }

  .guest-hero__metrics,
  .strategy-grid,
  .guest-insight-grid,
  .trust-grid {
    grid-template-columns: 1fr;
  }

  .guest-hero__metrics {
    grid-template-columns: repeat(2, 1fr);
  }

  .guest-hero__trust {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .asset-preview__numbers {
    flex-direction: column;
    gap: 12px;
  }
}

@media (max-width: 560px) {
  .guest-hero h1 {
    font-size: 34px;
  }

  .guest-hero__metrics {
    grid-template-columns: 1fr;
  }

  .guest-hero__actions {
    flex-direction: column;
  }

  .strategy-grid {
    grid-template-columns: 1fr;
  }
}
</style>
